import { Suspense, useEffect } from "react";

import { IGroup } from "pages/[tenant]/g/@types";
import useGroupContext from "pages/[tenant]/g/hooks/useGroupContext";

import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import { useAuthContext } from "shared/utils/hooks/useAuthContext";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";

import useCheckImageCover from "../../../hooks/useCheckImageCover";
import useHandleGroupFeatureFlag from "../../../hooks/useHandleGroupFeatureFlag";
import useProductPresentationService from "../../../hooks/useProductPresentationService";
import useTrackViewContent from "../../../hooks/useTrackViewContent";
import {
  AboveTheFolderSection,
  PresentationAditionalContentSection,
  PresentationAvalableOffersSection,
  PresentationFeaturesSection,
  PresentationModulesSection,
  PresentationSafeBuySection,
  PresentationTextContentSection,
  PresentationWarrantySection,
  PresentationWhatsAppSection,
} from "../../../lazyImports";
import { AboveTheFolderSectionFallback } from "../../organisms/AboveTheFolderSection/styles";
import { PresentationFeaturesSectionContentFallback } from "../../organisms/PresentationFeaturesSection/styles";
import {
  ProductPresentationTemplateContainer,
  ProductSafeSectionContainer,
} from "./styles";

interface IProductPresentationTemplateProps {
  groupData: IGroup;
}
const ProductPresentationTemplate = ({
  groupData,
}: IProductPresentationTemplateProps) => {
  const { setGroup, group } = useGroupContext();
  const { loggedUser } = useAuthContext();

  const { state } = useGlobalContext();
  const isMobileAppClient = state?.isMobileAppClient;

  const { getNexusByGroupId, getGroupWithPublicOffers } =
    useProductPresentationService();

  useEffect(
    function updateGroupOnUserLoggin() {
      if (group?.slug && loggedUser?.id) {
        getGroupWithPublicOffers({ group_slug: group?.slug });
      }
    },
    [getGroupWithPublicOffers, loggedUser?.id],
  );

  useEffect(
    function setInitialPageData() {
      if (groupData?.id) {
        setGroup(groupData);
      }
    },
    [groupData?.id, setGroup, groupData],
  );

  useEffect(
    function handleNexusByGroupId() {
      if (groupData?.id) {
        getNexusByGroupId({ groupId: groupData?.id });
      }
    },
    [getNexusByGroupId, groupData?.id],
  );

  useTrackViewContent(groupData);
  useHandleGroupFeatureFlag();
  useCheckImageCover();

  return (
    <ProductPresentationTemplateContainer id="product-presentation-template-container">
      <ComponentErrorBoundary>
        <Suspense fallback={<AboveTheFolderSectionFallback />}>
          <AboveTheFolderSection />
        </Suspense>
      </ComponentErrorBoundary>

      <Suspense fallback={<PresentationFeaturesSectionContentFallback />}>
        <ComponentErrorBoundary>
          <PresentationFeaturesSection />
        </ComponentErrorBoundary>
      </Suspense>

      <Suspense fallback={<></>}>
        <ComponentErrorBoundary>
          <PresentationTextContentSection />
        </ComponentErrorBoundary>
      </Suspense>

      <Suspense fallback={<></>}>
        <ComponentErrorBoundary>
          <PresentationModulesSection />
        </ComponentErrorBoundary>
      </Suspense>

      <Suspense fallback={<></>}>
        <ComponentErrorBoundary>
          <PresentationAditionalContentSection />
        </ComponentErrorBoundary>
      </Suspense>

      <Suspense fallback={<></>}>
        <ComponentErrorBoundary>
          <PresentationAvalableOffersSection />
        </ComponentErrorBoundary>
      </Suspense>

      {!isMobileAppClient && (
        <ProductSafeSectionContainer>
          <Suspense fallback={<></>}>
            <ComponentErrorBoundary>
              <PresentationSafeBuySection />
            </ComponentErrorBoundary>
          </Suspense>

          <Suspense fallback={<></>}>
            <ComponentErrorBoundary>
              <PresentationWarrantySection />
            </ComponentErrorBoundary>
          </Suspense>
        </ProductSafeSectionContainer>
      )}

      <Suspense fallback={<></>}>
        <ComponentErrorBoundary>
          <PresentationWhatsAppSection />
        </ComponentErrorBoundary>
      </Suspense>
    </ProductPresentationTemplateContainer>
  );
};

export default ProductPresentationTemplate;
